export type TimerType = 'TON' | 'TOF' | 'TP' | 'TONR';

export interface TimerRuntimeState {
  readonly id: string;
  type: TimerType;
  
  input: boolean;
  
  presetMs: number;
  elapsedMs: number;
  
  output: boolean;
  
  running: boolean;
  done: boolean;
}

export class TimerManager {
  private timers = new Map<string, TimerRuntimeState>();

  getTimer(id: string): TimerRuntimeState | undefined {
    return this.timers.get(id);
  }

  getOrCreateTimer(id: string, type: TimerType, presetMs: number): TimerRuntimeState {
    let timer = this.timers.get(id);
    if (!timer) {
      timer = {
        id,
        type,
        input: false,
        presetMs,
        elapsedMs: 0,
        output: false,
        running: false,
        done: false,
      };
      this.timers.set(id, timer);
    } else {
      // Update config if changed (in place to keep reference stable)
      timer.type = type;
      timer.presetMs = presetMs;
    }
    return timer;
  }

  resetTimer(id: string): void {
    const timer = this.timers.get(id);
    if (timer) {
      timer.elapsedMs = 0;
      timer.output = false;
      timer.running = false;
      timer.done = false;
    }
  }

  updateTimerInput(id: string, input: boolean): void {
    const timer = this.timers.get(id);
    if (!timer) return;
    
    // Evaluate edge logic
    const prevInput = timer.input;
    timer.input = input;

    if (timer.type === 'TON') {
      if (!prevInput && input) {
        // IN false -> true
        timer.running = true;
        timer.done = false;
        timer.elapsedMs = 0;
        timer.output = false;
      } else if (prevInput && !input) {
        // IN true -> false
        timer.running = false;
        timer.done = false;
        timer.elapsedMs = 0;
        timer.output = false;
      }
    } else if (timer.type === 'TONR') {
      if (input) {
        // Accumulate while input is true if not already done
        if (!timer.done) {
          timer.running = true;
        }
      } else {
        // Hold when input is false (do not reset elapsedMs!)
        timer.running = false;
      }
    } else if (timer.type === 'TOF') {
      if (input) {
        // IN is true
        timer.output = true;
        timer.running = false;
        timer.done = false;
        timer.elapsedMs = 0;
      } else if (prevInput) {
        // IN true -> false
        timer.running = true;
      }
    } else {
      if (!prevInput && input) {
        // Rising edge
        if (!timer.running && !timer.done) {
          timer.running = true;
          timer.elapsedMs = 0;
          timer.output = true;
        }
      }
    }
  }

  tick(deltaMs: number): void {
    for (const timer of this.timers.values()) {
      if (!timer.running) continue;

      timer.elapsedMs += deltaMs;

      if (timer.type === 'TON' || timer.type === 'TONR') {
        if (timer.elapsedMs >= timer.presetMs) {
          timer.elapsedMs = timer.presetMs; // Cap at PT
          timer.output = true;
          timer.running = false;
          timer.done = true;
        }
      } else if (timer.type === 'TOF') {
        if (timer.elapsedMs >= timer.presetMs) {
          timer.elapsedMs = timer.presetMs; // Cap at PT
          timer.output = false;
          timer.running = false;
          timer.done = true;
        }
      } else {
        if (timer.elapsedMs >= timer.presetMs) {
          timer.elapsedMs = timer.presetMs; // Cap at PT
          timer.output = false;
          timer.running = false;
          timer.done = true;
        }
      }
    }
    
    // Reset TP done state if input is false so it can trigger again on next rising edge
    for (const timer of this.timers.values()) {
      if (timer.type === 'TP' && timer.done && !timer.input) {
        timer.done = false;
        timer.elapsedMs = 0;
      }
    }
  }

  reset(): void {
    for (const timer of this.timers.values()) {
      timer.input = false;
      timer.elapsedMs = 0;
      timer.output = false;
      timer.running = false;
      timer.done = false;
    }
  }
  
  getSnapshot(): Record<string, { elapsedMs: number; presetMs: number; q: boolean; running: boolean; }> {
    const snap: Record<string, { elapsedMs: number; presetMs: number; q: boolean; running: boolean; }> = {};
    for (const [id, timer] of this.timers.entries()) {
      snap[id] = {
        elapsedMs: timer.elapsedMs,
        presetMs: timer.presetMs,
        q: timer.output,
        running: timer.running
      };
    }
    return snap;
  }
}
